import { Box, Typography, Container, Stack } from '@mui/material'
import { CircleOff } from 'lucide-react'

import { IconBadge } from 'components/UI/IconBadge'

/**
 * VRG-319 — what a genuine tag on a NON-ACTIVATED certificate resolves to.
 *
 * Reached only by redirect from `GET /verify/:uid` when the tag verified cryptographically
 * but the certificate it is bound to is `created` or `inactive` — see the
 * `certificate_not_activated` branch in `verify.public.controller.ts` for why the gate is a
 * denylist of those two statuses rather than `!== 'active'`.
 *
 * WHY IT IS ITS OWN ROUTE, and not `/tag-not-active`. That page (VRG-275) is for a HELD
 * REPLACEMENT TAG: a brand-new chip mailed to an owner, where the tag is the thing that is
 * not live and the owner is the person who can make it live. It is a claim flow, and it
 * carries a `certificateId` so it can act on one. This page is the opposite shape: the TAG
 * is fine, the CERTIFICATE has not been issued, there is no action a visitor can take, and
 * the person holding the item is as likely to be a stranger as the owner. Sharing a route
 * would mean one page answering two different questions with one message, and the message
 * that fits a mailed replacement ("confirm you received it") is meaningless here. The route
 * names WHICH thing is not active, which is the whole distinction: `/tag-not-active` versus
 * `/certificate-not-active`.
 *
 * ⚠️ THIS PAGE IS FOR NEVER-ACTIVATED ONLY. It must never be shown for a DORMANT
 * certificate — one whose brand lapsed, churned, or ended their contract. Certificates are
 * meant to last forever, and verification plus public certificate data are free forever;
 * the paid layer is the additional services. A dormant certificate keeps verifying and only
 * brand-side data collection stops, because a provenance record a commercial lapse can
 * switch off destroys value for third parties who never had a relationship with Vera.
 * Telling that consumer their genuine item "is not active" is the single worst thing this
 * page could do, so do not widen the gate that routes here without re-reading the
 * `certificate_not_activated` branch in `verify.public.controller.ts`. As built, dormancy
 * cannot reach this page at all: a lapsed subscription only downgrades the redirect
 * destination and `certificates.archived` is orthogonal to `status`, so neither writes the
 * statuses that route here.
 *
 * There is likewise NO revoked page, deliberately. Revoked (recalled, counterfeit, issued in
 * error) and dormant mean OPPOSITE things to a scanner — "do not trust this" versus "this is
 * real, the brand just isn't tracking it anymore" — and the schema cannot currently express
 * revoked at all. Building a page that guessed between them could tell someone their genuine
 * item is not valid, so it is reported rather than invented. See the VRG-319 task.
 *
 * ⚠️ WHY THE COPY DOES NOT SAY "YET" (load-bearing, not a style choice). "Not active yet"
 * asserts a direction of travel, and it becomes false the moment `inactive` stops meaning
 * only "never issued" — which is one wiring change away: `DeactivateCertificateUseCase`
 * returns an already-activated certificate to the same `inactive` status, nothing durable
 * records that it was ever active (no `activated_at`, no action log on either transition,
 * `is_active` moves in lockstep with `status`), and the endpoint plus its client mutation
 * already exist with no component calling them. So the copy states the condition and stops.
 * Do NOT "improve" it to "not active yet" or "not issued yet" before the status is split.
 *
 * Deliberately absent, same rule as `pages/TagReplaced` (read that file's header first):
 *   - any accusation, or wording implying the item is counterfeit. An unactivated
 *     certificate is usually a perfectly legitimate item that the brand has not issued yet.
 *   - any narration of what the brand did or did not do
 *   - Vera's identity or a Vera support path. The visitor believes they are dealing with
 *     the brand, and the brand is who can actually look the item up.
 *
 * The page carries no Vera branding of its own because it is served from the company's
 * Vera-hosted installation, on the brand's subdomain, and `App.tsx` themes the whole app
 * from that subdomain. It therefore inherits the brand's identity by construction.
 */
export default function CertificateNotActive() {
  return (
    <Container maxWidth='sm'>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '80vh',
          textAlign: 'center',
        }}
      >
        <Stack spacing={3} alignItems='center'>
          {/*
            The shared `IconBadge` — the grey circle every state screen in this app opens
            with. Both choices follow this page's doctrine above rather than taste:

            GLYPH. `CircleOff` states the condition and stops, which is the same discipline
            the copy is held to. Every clock/hourglass variant was rejected under the "does
            not say YET" rule in the header: a clock IS a direction-of-travel claim, and it
            becomes false the moment `inactive` stops meaning only "never issued". A
            document glyph with an X or a minus was rejected for the other rule — it reads
            as rejected or revoked, which is the one thing this page must never imply about
            a legitimate item the brand simply has not issued yet.

            TONE. The brand tone, not the alert red. An unactivated certificate is not a
            fault; the red is reserved for the pages where something has actually broken.
          */}
          <IconBadge icon={CircleOff} />

          <Stack spacing={2} alignItems='center'>
            <Typography variant='h4' component='h1'>
              This certificate is not active
            </Typography>

            {/*
              Separate sentences rather than one joined by an em-dash (§15). lineHeight 1.5
              so the copy does not read dense on a phone, which is the only device this page
              is ever seen on.
            */}
            <Typography variant='body1' color='text.secondary' sx={{ lineHeight: 1.5 }}>
              The digital certificate for this item is not currently active. If you have
              questions about this item, please contact the brand you bought it from. They
              can look it up for you.
            </Typography>
          </Stack>
        </Stack>
      </Box>
    </Container>
  )
}
